/**
 * <rx-configurator> — lens selection steps and price.
 * Reads its JSON from [data-rx-data] and keeps state in RxState.
 * The material step hides itself when a vision type offers only one.
 */
import { getRxState, lensAxisValues, resolveLensVariant, formatCents } from '#rx/core';
import { addRxBundle } from '#rx/cart';

const AXES = ['material', 'finish', 'color'];
const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const escapeAttr = escapeHtml;

class RxConfigurator extends HTMLElement {
  connectedCallback() {
    const raw = this.closest('[data-rx-root]')?.querySelector('[data-rx-data]');
    if (!raw) return;

    this.data = JSON.parse(raw.textContent);
    this.state = getRxState();
    this.state.set('frame', this.data.frame);
    this.stepsEl = this.querySelector('[data-rx-steps]');
    this.summaryEl = this.querySelector('[data-rx-summary]');

    this.state.addEventListener('rx:change', () => this.render());

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-rx-open-prescription]')) {
        document.dispatchEvent(new CustomEvent('rx:prescription:open'));
        return;
      }
      this.onChoose(event);
    });

    // The drawer writes the prescription into the shared state and announces it.
    // Buying is the point of that step, so the bundle goes to the cart there
    // and then — the shopper is not sent back to the page to press again.
    document.addEventListener('rx:prescription-submit', () => this.addToCartAfterPrescription());

    this.keepRxViewOnSiblingLinks();

    this.applyDefaults();
    this.render();
  }


  /**
   * Keep colour switches inside the prescription view.
   *
   * The sibling links point at plain product URLs, so picking another colour
   * dropped the shopper back onto the regular page and silently lost the lens
   * they had configured. The links are rewritten to carry `?view=rx`, and an
   * observer re-applies it because the theme re-renders the swatches when the
   * variant changes.
   */
  keepRxViewOnSiblingLinks() {
    const apply = () => {
      for (const link of document.querySelectorAll('.sibling__link, [data-sibling-swapper]')) {
        const href = link.getAttribute('href');
        if (!href || !href.includes('/products/')) continue;
        try {
          const url = new URL(href, window.location.origin);
          if (url.searchParams.get('view') === 'rx') continue;
          url.searchParams.set('view', 'rx');
          link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
        } catch {
          // A malformed href is the theme's business, not ours.
        }
      }
    };

    apply();

    const host = document.querySelector('.product__siblings') ?? document.querySelector('main');
    if (!host) return;
    new MutationObserver(apply).observe(host, { childList: true, subtree: true, attributeFilter: ['href'] });
  }

  /** Vision types, in section block order. */
  get visionTypes() {
    const seen = new Map();
    for (const lens of this.data.lenses) {
      if (lens.unparsed || seen.has(lens.visionType)) continue;
      seen.set(lens.visionType, lens.visionLabel || lens.visionType);
    }
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }

  /** Values of an axis given the current selection. */
  valuesFor(axis) {
    const filter = { visionType: this.state.visionType };
    for (const earlier of AXES) {
      if (earlier === axis) break;
      filter[earlier] = this.state[earlier];
    }
    return lensAxisValues(this.data.lenses, axis, filter);
  }

  /**
   * Fill every axis that has no valid value with the first one on offer, so the
   * configurator is never half-built: the shopper always sees a complete lens,
   * its price and a way to buy.
   */
  applyDefaults() {
    const state = this.state;

    if (!state.visionType) {
      const first = this.visionTypes[0];
      if (!first) return;
      state.visionType = first.value;
    }

    // Axes narrow left to right, so resolving them in order keeps each choice valid.
    for (const axis of AXES) {
      const values = this.valuesFor(axis);
      if (values.length === 0) {
        state[axis] = null;
        continue;
      }
      if (!values.includes(state[axis])) state[axis] = values[0];
    }

    state.lensVariant = this.resolveLens();
  }

  resolveLens() {
    const { visionType, material, finish, color } = this.state;
    return resolveLensVariant(this.data.lenses, { visionType, material, finish, color });
  }

  onChoose(event) {
    const button = event.target.closest('[data-rx-choose]');
    if (!button) return;

    const axis = button.dataset.rxChoose;
    const value = button.dataset.rxValue;

    if (axis === 'visionType') {
      // Changing vision type clears everything below it: axes differ per type.
      this.state.material = null;
      this.state.finish = null;
      this.state.color = null;
      this.state.visionType = value;
    } else {
      // Reset axes below the one chosen, or a non-existent combination survives.
      let below = false;
      for (const other of AXES) {
        if (below) this.state[other] = null;
        if (other === axis) below = true;
      }
      this.state[axis] = value;
    }

    // Re-fill what was cleared, then announce once so the UI redraws complete.
    this.applyDefaults();
    this.state.set('lensVariant', this.resolveLens());
  }

  /** Vision types render as underlined tabs, the way the theme styles its Fit selector. */
  renderTabs(choices, selected) {
    const tabs = choices.map(({ value, label }) => `
      <button type="button" class="rx-tab-item${value === selected ? ' is-selected' : ''}"
              data-rx-choose="visionType" data-rx-value="${escapeAttr(value)}">${escapeHtml(label)}</button>`).join('');
    return `<div class="rx-tabs" role="tablist">${tabs}</div>`;
  }

  /** Material and finish render as a segmented row: the chosen one is filled. */
  renderSegments(axis, values, selected) {
    if (values.length < 2) return '';
    const items = values.map((value) => `
      <button type="button" class="rx-seg${value === selected ? ' is-selected' : ''}"
              data-rx-choose="${axis}" data-rx-value="${escapeAttr(value)}">${escapeHtml(value)}</button>`).join('');
    return `<div class="rx-segments rx-segments--${axis}">${items}</div>`;
  }

  /** Colour swatches use the lens variant image, so the shopper sees the actual tint. */
  renderSwatches(values, selected) {
    const swatches = values.map((value) => {
      const entry = this.data.lenses.find((l) =>
        l.visionType === this.state.visionType && l.finish === this.state.finish && l.color === value);
      const image = entry?.image ? `background-image:url(${entry.image})` : '';
      return `
        <button type="button" class="rx-swatch${value === selected ? ' is-selected' : ''}"
                data-rx-choose="color" data-rx-value="${escapeAttr(value)}"
                title="${escapeAttr(value)}" aria-label="${escapeAttr(value)}">
          <span class="rx-swatch__chip" style="${image}"></span>
        </button>`;
    }).join('');
    return `<div class="rx-swatches">${swatches}</div>`;
  }

  /** Copy for the chosen lens. It rides on the variant, so each finish+colour
   *  pair gets its own wording without the component knowing the catalogue. */
  renderLensCopy() {
    const lens = this.state.lensVariant;
    if (!lens) return '';
    const title = lens.copyTitle;
    const text = lens.copyText;
    if (!title && !text) return '';
    return `
      ${title ? `<p class="rx-panel__title">${escapeHtml(title)}</p>` : ''}
      ${text ? `<p class="rx-panel__text">${escapeHtml(text)}</p>` : ''}`;
  }

  render() {
    const state = this.state;
    let html = this.renderTabs(this.visionTypes, state.visionType);

    if (state.visionType) {
      for (const axis of AXES) {
        const values = this.valuesFor(axis);
        if (values.length === 0) continue;

        if (axis === 'color') {
          // Swatches and the finish copy share one bordered panel, as in the design.
          html += `<div class="rx-panel">${this.renderSwatches(values, state.color)}${this.renderLensCopy()}</div>`;
        } else {
          html += this.renderSegments(axis, values, state[axis]);
        }
      }
    }

    this.stepsEl.innerHTML = html;
    this.renderSummary();
  }

  renderSummary() {
    const state = this.state;
    const needsRx = state.visionType && state.visionType !== 'non_rx';
    const lens = state.lensVariant;
    const hasPrescription = Boolean(state.prescription?.method);

    if (!lens) {
      this.summaryEl.innerHTML = '';
      return;
    }

    const rows = `
      <dl class="rx-total">
        <div class="rx-total__row"><dt>Frame</dt><dd>${formatCents(state.frame?.price || 0)}</dd></div>
        <div class="rx-total__row"><dt>Lens</dt><dd>${formatCents(lens.price)}</dd></div>
        <div class="rx-total__row rx-total__row--sum"><dt>Subtotal</dt><dd>${formatCents(state.totalPrice)}</dd></div>
      </dl>`;

    // With a prescription the drawer finishes the purchase itself, so the only
    // call to action here is the one that opens it. A second button would ask
    // the shopper to confirm something they already confirmed.
    const prescriptionCta = needsRx
      ? `<button type="button" class="btn btn--primary btn--solid btn--full rx-cta" data-rx-open-prescription>${
          hasPrescription ? 'Edit prescription' : 'Add prescription'
        }</button>`
      : '';
    const prescriptionNote = needsRx && hasPrescription
      ? state.prescription.method === 'later'
        ? '<p class="rx-summary__rx">You\u2019ll add your prescription after checkout.</p>'
        : '<p class="rx-summary__rx">Prescription added.</p>'
      : '';
    // Non-prescription lenses have nothing to confirm, so they keep a plain buy button.
    const addToCart = !needsRx && state.isComplete
      ? `<button type="button" class="btn btn--primary btn--solid btn--full rx-cta" data-rx-add${this.adding ? ' disabled' : ''}>${
          this.adding ? 'Adding\u2026' : 'Add to cart'
        }</button>`
      : '';

    const cartError = this.cartError
      ? `<p class="rx-error">${escapeHtml(this.cartError)}</p>`
      : '';

    this.summaryEl.innerHTML = rows + prescriptionCta + prescriptionNote + addToCart + cartError;

    const add = this.summaryEl.querySelector('[data-rx-add]');
    if (add) add.addEventListener('click', () => this.addToCart(add));
  }

  async addToCartAfterPrescription() {
    // Guard against a second submit landing while the first is still in flight.
    if (this.adding || !this.state.isComplete) return;
    await this.addToCart();
  }

  async addToCart(button) {
    if (this.adding) return;
    this.adding = true;
    if (button) button.disabled = true;
    this.renderSummary();
    this.cartError = null;
    try {
      await addRxBundle(this.state, this.data.proxy);
    } catch (error) {
      // Held in state, not written into the summary: the re-render below would
      // wipe an injected message, and the shopper would see nothing happen.
      this.cartError = error.message;
    } finally {
      this.adding = false;
      if (button) button.disabled = false;
      this.renderSummary();
    }
  }
}

customElements.define('rx-configurator', RxConfigurator);
